/** /forgot-password（公開）— 輸入 Email 申請重設連結；不洩漏帳號是否存在。 */
import { useState } from "react";
import { useLocation } from "wouter";
import { ArrowLeft, MailCheck, Send } from "lucide-react";
import { trpc } from "../../lib/trpc";
import { useLanguage } from "../../contexts/LanguageContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Card, CardContent } from "../../components/ui/card";
import { LogoImage } from "../../components/Logo";
import { ThemeToggle } from "../../components/theme-toggle";
import { FormNotice } from "../../components/account/FormNotice";

export default function ForgotPassword() {
  const [, setLocation] = useLocation();
  const { t } = useLanguage();
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<"email" | "unavailable" | null>(null);

  const m = trpc.account.requestPasswordReset.useMutation({
    onSuccess: (r) => setResult(r.delivery),
    onError: (e) => setError(e.message),
  });

  return (
    <div className="min-h-screen bg-background">
      <header className="flex items-center justify-between border-b border-border px-6 py-4 md:px-10">
        <button onClick={() => setLocation("/login")} className="transition-opacity hover:opacity-80">
          <LogoImage className="h-8" />
        </button>
        <ThemeToggle />
      </header>
      <main className="mx-auto max-w-md px-6 py-12">
        <div className="animate-fade-up space-y-6">
          <div className="space-y-2">
            <p className="font-mono text-[12px] uppercase tracking-[0.18em] text-primary">
              {t({ "zh-TW": "忘記密碼", "en-US": "Forgot password", "zh-CN": "忘记密码" })}
            </p>
            <h1 className="font-display text-3xl font-medium tracking-tight">
              {t({ "zh-TW": "重設你的密碼", "en-US": "Reset your password", "zh-CN": "重设你的密码" })}
            </h1>
            <p className="text-[13px] leading-relaxed text-muted-foreground">
              {t({
                "zh-TW": "輸入註冊時的工作 Email，我們會寄出 60 分鐘內有效的重設連結。",
                "en-US": "Enter your work email and we'll send a reset link valid for 60 minutes.",
                "zh-CN": "输入注册时的工作 Email，我们会寄出 60 分钟内有效的重设连结。",
              })}
            </p>
          </div>

          <Card>
            <CardContent className="p-6">
              {result === "email" ? (
                <div className="space-y-4">
                  <FormNotice kind="success">
                    <span className="flex items-center gap-1.5"><MailCheck className="h-3.5 w-3.5" />
                      {t({
                        "zh-TW": `若 ${email} 是本平台帳號，重設連結已寄出，請檢查收件匣與垃圾郵件。`,
                        "en-US": `If ${email} belongs to an account, a reset link is on its way. Check your inbox and spam folder.`,
                        "zh-CN": `若 ${email} 是本平台帐号，重设连结已寄出，请检查收件匣与垃圾邮件。`,
                      })}
                    </span>
                  </FormNotice>
                  <Button variant="outline" className="w-full" onClick={() => setLocation("/login")}>
                    <ArrowLeft className="h-3.5 w-3.5" />
                    {t({ "zh-TW": "回登入頁", "en-US": "Back to sign in", "zh-CN": "回登入页" })}
                  </Button>
                </div>
              ) : result === "unavailable" ? (
                <div className="space-y-4">
                  <FormNotice kind="info">
                    {t({
                      "zh-TW": "目前系統尚未開通自動寄信，無法寄出重設連結。請聯絡平台管理員為你重設密碼，你會收到一組臨時密碼，登入後再自行更改。",
                      "en-US": "Automatic email isn't enabled yet, so we can't send a reset link. Please contact the platform admin to reset your password — you'll get a temporary password to change after signing in.",
                      "zh-CN": "目前系统尚未开通自动寄信，无法寄出重设连结。请联系平台管理员为你重设密码，你会收到一组临时密码，登入后再自行更改。",
                    })}
                  </FormNotice>
                  <Button variant="outline" className="w-full" onClick={() => setLocation("/login")}>
                    <ArrowLeft className="h-3.5 w-3.5" />
                    {t({ "zh-TW": "回登入頁", "en-US": "Back to sign in", "zh-CN": "回登入页" })}
                  </Button>
                </div>
              ) : (
                <form
                  className="space-y-4"
                  onSubmit={(e) => {
                    e.preventDefault();
                    setError(null);
                    m.mutate({ email: email.trim() });
                  }}
                >
                  <div className="space-y-1.5">
                    <Label htmlFor="fp-email" className="text-[12px]">Email</Label>
                    <Input id="fp-email" type="email" required autoComplete="email" autoFocus placeholder="you@company.com" value={email} onChange={(e) => setEmail(e.target.value)} />
                  </div>
                  {error && <FormNotice kind="error">{error}</FormNotice>}
                  <Button type="submit" className="w-full" size="lg" disabled={m.isPending || email.trim() === ""}>
                    <Send className="h-4 w-4" />
                    {m.isPending ? t({ "zh-TW": "送出中…", "en-US": "Sending…", "zh-CN": "送出中…" }) : t({ "zh-TW": "寄送重設連結", "en-US": "Send reset link", "zh-CN": "寄送重设连结" })}
                  </Button>
                  <button type="button" onClick={() => setLocation("/login")} className="flex w-full items-center justify-center gap-1 text-[12px] text-muted-foreground transition-colors hover:text-primary">
                    <ArrowLeft className="h-3 w-3" />
                    {t({ "zh-TW": "回登入頁", "en-US": "Back to sign in", "zh-CN": "回登入页" })}
                  </button>
                </form>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
